/**
 * 知识点 Repository — 驼峰的统一数据操作
 *
 * 管理 word（生词）、snippet（段落收藏）、note（批注）三种知识点。
 * 数据存 IndexedDB（key: 'hump-knowledge'）。
 *
 * 对齐新定义：驼峰 = 知识点集合（非仅生词本）
 */

import type {
  KnowledgeItem,
  KnowledgeType,
  KnowledgeBase,
} from '@hump/shared-types';
// #15 循环依赖清理：原为动态 import。
// 经核查 knowledge.ts → ../dictionary → ./data（+ 动态 ../ai/provider → ../ai/config）
// 这条链上无任何模块再引用回 knowledge.ts，不存在循环依赖，故改为静态 import。
import { lookupWord } from '../dictionary';

const STORAGE_KEY = 'hump-knowledge';
const KB_VERSION = '1.0';

const EMPTY_KB: KnowledgeBase = {
  version: KB_VERSION,
  items: [],
};

/** 迁移函数链（版本升级时追加） */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const KB_MIGRATORS: Record<string, (kb: KnowledgeBase) => KnowledgeBase> = {};

/** 检查并执行数据迁移 */
function migrateKB(kb: KnowledgeBase): KnowledgeBase {
  const current = kb;
  if (!current.version) current.version = '1.0'; // 无版本的旧数据补上
  return current;
}

/** 读取知识点集合 */
async function readKB(): Promise<KnowledgeBase> {
  const { get: idbGet } = await import('idb-keyval');
  const kb = await idbGet<KnowledgeBase>(STORAGE_KEY);
  if (!kb) return EMPTY_KB;
  return migrateKB(kb);
}

/** 写入知识点集合 */
async function writeKB(kb: KnowledgeBase): Promise<void> {
  const { set: idbSet } = await import('idb-keyval');
  await idbSet(STORAGE_KEY, kb);
}

function generateId(): string {
  return 'k_' + crypto.randomUUID();
}

export const knowledgeRepository = {
  /** 获取全部知识点 */
  async getAll(): Promise<KnowledgeItem[]> {
    const kb = await readKB();
    return kb.items.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },

  /** 按类型筛选 */
  async getByType(type: KnowledgeType): Promise<KnowledgeItem[]> {
    const kb = await readKB();
    return kb.items.filter((i) => i.type === type).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },

  /** 搜索 */
  async search(query: string): Promise<KnowledgeItem[]> {
    const kb = await readKB();
    const q = query.toLowerCase();
    return kb.items
      .filter((i) => {
        const haystack = [i.word, i.text, i.note, ...(i.definitions ?? [])].join(' ').toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },

  /** 添加生词（同词同文档去重：已存在则更新释义，不重复创建） */
  async addWord(
    word: string,
    docId: string,
    blockId: string | undefined,
    docTitle: string | undefined,
    context: string,
    definitions?: string[],
    phonetic?: string,
    pos?: string
  ): Promise<KnowledgeItem> {
    const kb = await readKB();

    // 查离线词典补充释义（#15 已改为静态 import，无需 try/catch 包裹模块加载）
    if (!definitions || definitions.length === 0) {
      try {
        const entry = lookupWord(word);
        if (entry) {
          definitions = entry.definitions;
          phonetic = phonetic ?? entry.phonetic;
          pos = pos ?? entry.pos;
        }
      } catch {
        // 词典不可用（数据加载异常等）
      }
    }

    // 去重：同词（不区分大小写）已存在则补充释义
    const normalized = word.toLowerCase();
    const existing = kb.items.find(
      (i) => i.type === 'word' && i.word?.toLowerCase() === normalized
    );
    if (existing) {
      // 补充缺失的释义
      if (definitions && definitions.length > 0 && (!existing.definitions || existing.definitions.length === 0)) {
        existing.definitions = definitions;
        existing.phonetic = existing.phonetic ?? phonetic;
        existing.pos = existing.pos ?? pos;
      }
      existing.updatedAt = new Date().toISOString();
      await writeKB(kb);
      return existing;
    }

    const item: KnowledgeItem = {
      id: generateId(),
      type: 'word',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      docId,
      blockId,
      docTitle,
      word,
      definitions: definitions ?? [],
      phonetic,
      pos,
      context,
    };

    kb.items.push(item);
    await writeKB(kb);
    return item;
  },

  /** 添加段落收藏 */
  async addSnippet(
    text: string,
    docId: string,
    blockId: string | undefined,
    docTitle: string | undefined
  ): Promise<KnowledgeItem> {
    const kb = await readKB();
    const item: KnowledgeItem = {
      id: generateId(),
      type: 'snippet',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      docId,
      blockId,
      docTitle,
      text,
    };
    kb.items.push(item);
    await writeKB(kb);
    return item;
  },

  /** 添加批注 */
  async addNote(
    note: string,
    docId: string,
    blockId: string | undefined,
    docTitle: string | undefined,
    context?: string
  ): Promise<KnowledgeItem> {
    const kb = await readKB();
    const item: KnowledgeItem = {
      id: generateId(),
      type: 'note',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      docId,
      blockId,
      docTitle,
      note,
      context,
    };
    kb.items.push(item);
    await writeKB(kb);
    return item;
  },

  /** 更新知识点 */
  async update(id: string, updates: Partial<KnowledgeItem>): Promise<void> {
    const kb = await readKB();
    const item = kb.items.find((i) => i.id === id);
    if (item) {
      Object.assign(item, updates, { updatedAt: new Date().toISOString() });
      await writeKB(kb);
    }
  },

  /** 删除知识点 */
  async remove(id: string): Promise<void> {
    const kb = await readKB();
    kb.items = kb.items.filter((i) => i.id !== id);
    await writeKB(kb);
  },

  /** 文档删除时标记关联知识点的来源已失效（不物理删除，保留历史） */
  async markDeletedByDoc(docId: string): Promise<void> {
    const kb = await readKB();
    let changed = false;
    for (const item of kb.items) {
      if (item.docId === docId) {
        // 来源文档已删除——在 docTitle 标记，UI 可标灰
        item.docTitle = (item.docTitle ?? '') + '（已删除）';
        item.updatedAt = new Date().toISOString();
        changed = true;
      }
    }
    if (changed) await writeKB(kb);
  },
};
